import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

// The GIS script tag is loaded in frontend/public/index.html (async defer).
// window.google.accounts.id becomes available once the script finishes loading.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GSI_POLL_INTERVAL_MS = 150;
const GSI_POLL_TIMEOUT_MS = 8000;

const LoginPage = () => {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const googleBtnRef = useRef(null);
  const [status, setStatus] = useState(GOOGLE_CLIENT_ID ? 'loading' : 'no-client-id');
  const [errorMsg, setErrorMsg] = useState('');

  // Google calls this with the ID token (the "credential") after the user
  // picks an account. We forward it to the backend via useAuth.loginWithGoogle.
  const handleCredential = async (response) => {
    if (!response || !response.credential) {
      toast.error('Google did not return a credential');
      return;
    }
    try {
      const result = await loginWithGoogle(response.credential);
      if (result && result.user && result.user.isNewUser) {
        toast.success('Welcome to MME — your API key is ready');
      } else {
        toast.success('Signed in');
      }
      navigate('/memories', { replace: true });
    } catch (err) {
      const backendMsg = err && err.response && err.response.data && err.response.data.error;
      const msg = backendMsg || (err && err.message) || 'Sign-in failed. Please try again.';
      setErrorMsg(msg);
      toast.error(msg);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setStatus('no-client-id');
      return undefined;
    }

    let cancelled = false;
    let elapsed = 0;

    const tryInit = () => {
      if (cancelled) return;

      if (window.google && window.google.accounts && window.google.accounts.id) {
        try {
          window.google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: handleCredential,
            auto_select: false,
            use_fedcm_for_prompt: true,
          });

          if (googleBtnRef.current) {
            window.google.accounts.id.renderButton(googleBtnRef.current, {
              type: 'standard',
              theme: 'filled_black',
              size: 'large',
              text: 'continue_with',
              shape: 'rectangular',
              logo_alignment: 'left',
              width: 320,
            });
          }
          setStatus('ready');
        } catch (e) {
          setStatus('error');
          setErrorMsg((e && e.message) || 'Google SDK failed to initialize');
        }
        return;
      }

      elapsed += GSI_POLL_INTERVAL_MS;
      if (elapsed >= GSI_POLL_TIMEOUT_MS) {
        setStatus('error');
        setErrorMsg('Google sign-in is not available right now. Try refreshing the page.');
        return;
      }
      setTimeout(tryInit, GSI_POLL_INTERVAL_MS);
    };
    tryInit();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Sign in — MME</title>
      </Helmet>

      <div className="min-h-screen bg-gray-950 text-white flex">
        {/* Left — branding */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center p-12 border-r border-white/5">
          <div className="max-w-md">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-gray-500 hover:text-white transition-colors mb-12"
            >
              <FiArrowLeft className="mr-1" size={14} />
              Back to home
            </Link>

            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-lg font-bold">M</span>
              </div>
              <span className="text-2xl font-bold tracking-tight">MME</span>
            </div>

            <h1 className="text-4xl font-bold tracking-tight leading-tight mb-4">
              Memory that learns
              <br />
              <span className="text-gray-500">what matters</span>
            </h1>

            <p className="text-gray-500 leading-relaxed">
              Sign in with your Google account to get started. We'll provision an API key
              for you automatically — no forms to fill, no keys to paste.
            </p>

            <div className="mt-12 space-y-4">
              {[
                { label: 'Auto-tagging', desc: 'LLM extracts tags from content automatically' },
                { label: 'Scored retrieval', desc: 'Memories ranked by relevance and recency' },
                { label: 'Feedback loop', desc: 'Useful memories get promoted, stale ones decay' },
              ].map((item) => (
                <div key={item.label} className="flex items-start space-x-3">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                  <div>
                    <span className="text-sm font-medium">{item.label}</span>
                    <span className="text-sm text-gray-600 ml-2">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right — sign-in form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-sm">
            <Link
              to="/"
              className="lg:hidden inline-flex items-center text-sm text-gray-500 hover:text-white transition-colors mb-8"
            >
              <FiArrowLeft className="mr-1" size={14} />
              Back
            </Link>

            <h2 className="text-2xl font-bold mb-1">Sign in</h2>
            <p className="text-gray-500 text-sm mb-8">
              Continue with your Google account. Sign-up happens automatically the first time.
            </p>

            <div className="flex flex-col items-center space-y-4">
              {/* Google renders its real button into this div via renderButton */}
              <div
                ref={googleBtnRef}
                className="flex justify-center min-h-[44px]"
                aria-label="Google sign-in button"
              />

              {status === 'loading' && (
                <div className="text-xs text-gray-600">Loading Google sign-in…</div>
              )}

              {status === 'no-client-id' && (
                <div className="w-full px-4 py-3 bg-yellow-900/20 border border-yellow-800/40 rounded-lg text-sm text-yellow-200">
                  Google sign-in is not configured on this deployment.
                  Set <code className="font-mono text-xs">REACT_APP_GOOGLE_CLIENT_ID</code> and redeploy.
                </div>
              )}

              {status === 'error' && (
                <div className="w-full px-4 py-3 bg-red-900/30 border border-red-800/50 rounded-lg text-sm text-red-200">
                  {errorMsg || 'Google sign-in is not available right now.'}
                </div>
              )}
            </div>

            <p className="mt-8 text-center text-xs text-gray-600">
              By continuing you agree to our{' '}
              <Link to="/terms" className="text-gray-400 hover:text-white underline underline-offset-2">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-gray-400 hover:text-white underline underline-offset-2">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
